import { CodeBlock } from './CodeBlock';
import './codeblock.scss';

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: 'Fixing 404 Errors When Refreshing React Pages in cPanel',
  description: "Ever hit refresh on your React app's page and got a 404 error? Here's how I fixed it in cPanel.",
  image: 'https://nazmus.me/images/404Error.png',
  author: {
    '@type': 'Person',
    name: 'Nazmus Sakib'
  },
  publisher: {
    '@type': 'Organization',
    name: 'Nazmus Blog',
    logo: {
      '@type': 'ImageObject',
      url: 'https://nazmus.me/images/logo.png'
    }
  },
  datePublished: '2025-04-01',
  dateModified: '2025-04-01',
  url: 'https://nazmus.me/blogs/refresh-issues-in-cpanel'
};
export const RefreshIssuesInCpanel = () => {
  return (
    <>
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      <div className="blog-page-container">
        <div className="blog-page-content">
          {/* Hero Section with Image */}
          <div className="blog-image-container">
            <img src="/images/404Error.png" alt="404 Not Found Illustration" className="blog-image" />
          </div>

          {/* Blog Header */}
          <header className="blog-header">
            <h1 className="blog-title">Fixing 404 Errors When Refreshing React Pages in cPanel</h1>
            <p className="blog-description">
              Ever hit refresh on your React app's page and got a 404 error? Here's how I fixed it in cPanel.
            </p>
            <div className="blog-meta">
              <span className="blog-date">Published: April 2025</span>
              <span className="blog-read-time">5 min read</span>
            </div>
          </header>

          <hr className="blog-divider" />

          {/* Introduction */}
          <section className="blog-section">
            <p className="blog-paragraph">
              Picture this: You've just deployed your React app to cPanel. Everything looks perfect - your routes work,
              your components render beautifully, and you're feeling pretty good about yourself. But then you hit
              refresh on a page like `/about` or `/contact`, and boom! You're staring at a 404 error page.
            </p>
            <p className="blog-paragraph">
              This happened to me recently with my portfolio site. I spent hours trying to figure out why my pages
              worked fine when navigating through the app, but broke when refreshing. If you're in the same boat, I've
              got good news - there's a simple fix!
            </p>
          </section>

          {/* Problem Section */}
          <section className="blog-section">
            <h2 className="blog-section-title">Why Does This Happen?</h2>
            <p className="blog-section-content">
              Here's the deal: When you click around your React app, React Router handles everything smoothly on the
              client side. But when you hit refresh, your browser asks the server directly for that page. Since there's
              no actual `/about` folder on your server (it's just React Router magic), the server goes "404 - can't find
              that!"
            </p>
          </section>

          <section className="blog-section">
            <h2 className="blog-section-title">Finding the .htaccess File</h2>
            <p className="blog-section-content">
              The fix involves editing a file called `.htaccess`. It's usually hidden in cPanel, so here's how to find
              it:
            </p>
            <img src="/images/cpanel_setting.png" alt="cpanel setting Illustration" className="blog-image" />
            <ul className="blog-section-list">
              <li className="blog-section-list-item">Go to your cPanel dashboard</li>
              <li className="blog-section-list-item">Click the settings icon (⚙️) for your website</li>
              <li className="blog-section-list-item">Look for "Show Hidden Files" and check that box</li>
              <li className="blog-section-list-item">Save the settings</li>
            </ul>
            <p className="blog-section-content">
              Now you should see the `.htaccess` file in your public_html folder. If you don't see it, you might need to
              create one.
            </p>
          </section>

          {/* Solution Section */}
          <section className="blog-section">
            <h2 className="blog-section-title">The Fix</h2>
            <p className="blog-section-content">Here's what you need to do:</p>
            <ul className="blog-section-list">
              <li className="blog-section-list-item">Open the `.htaccess` file</li>
              <li className="blog-section-list-item">Click the Edit button in the top navbar</li>
              <li className="blog-section-list-item">Paste this code:</li>
            </ul>
            <CodeBlock
              language="apacheconf"
              code={`
              Options -MultiViews
              RewriteEngine On
              RewriteCond %{REQUEST_FILENAME} !-f
              RewriteRule ^ index.html [QSA,L]

              <IfModule mime_module>
                AddHandler application/x-httpd-ea-php81 .php .php8 .phtml
              </IfModule>
            `}
            />
            <p className="blog-section-content">
              This code tells your server: "Hey, if someone asks for a page that doesn't exist as a real file, just give
              them the index.html file instead." That way, your React app can take over and show the right page.
            </p>
          </section>

          {/* Conclusion */}
          <section className="blog-section">
            <h2 className="blog-section-title">That's It!</h2>
            <p className="blog-section-content">
              After saving these changes, try refreshing your pages again. They should work perfectly now! No more 404
              errors when hitting refresh.
            </p>
            <div className="blog-callout">
              <p className="blog-callout-text">
                Quick Tip: If you're still seeing 404 errors after making these changes, try clearing your browser cache
                or doing a hard refresh (Ctrl + Shift + R or Cmd + Shift + R).
              </p>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};
